import React, { useState } from "react";
import ErrorRow from "Common/ErrorRow";
import BreadCrumb from "Common/BreadCrumb";
import { Wallet, Search, RefreshCw } from "lucide-react";
import { ToastContainer } from "react-toastify";
import useAllWallets from "hooks/useAllWallets";
import { AdminWallet } from "services/walletAdmin.service";

const VBA_STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  expired: "bg-gray-100 text-gray-500",
  blacklisted: "bg-red-100 text-red-700",
};

const Wallets = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const { data: response, isLoading, isError, refetch } = useAllWallets({ page, limit: 20 });

  const wallets: AdminWallet[] = response?.data?.data ?? [];
  const total = response?.data?.total ?? 0;
  const totalPages = Math.ceil(total / 20);

  const filtered = search
    ? wallets.filter((w) => {
        const q = search.toLowerCase();
        return (
          w.walletNumber?.toLowerCase().includes(q) ||
          w.user?.firstName?.toLowerCase().includes(q) ||
          w.user?.lastName?.toLowerCase().includes(q) ||
          w.user?.email?.toLowerCase().includes(q) ||
          w.vba?.accountNumber?.includes(q)
        );
      })
    : wallets;

  const totalBalance = wallets.reduce((sum, w) => sum + (w.balance || 0), 0);

  return (
    <React.Fragment>
      <BreadCrumb title="Wallets" pageTitle="Finance" />
      <ToastContainer closeButton={false} limit={1} />

      {/* Summary card */}
      <div className="mb-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-4">
          <div className="p-3 rounded-lg bg-indigo-50">
            <Wallet size={20} className="text-indigo-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{total}</p>
            <p className="text-sm text-gray-500">Total Wallets</p>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center gap-4">
          <div className="p-3 rounded-lg bg-green-50">
            <Wallet size={20} className="text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              ₦{totalBalance.toLocaleString()}
            </p>
            <p className="text-sm text-gray-500">Combined Balance (page)</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
            <input
              type="text"
              placeholder="Filter this page by wallet no., name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            />
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="size-4" /> Refresh
          </button>
          <span className="sm:ml-auto text-sm text-gray-500">{total} total</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table data-stack className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Wallet No.</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Owner</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Balance</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">VBA Account</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">VBA Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <ErrorRow colSpan={6} label="Couldn't load wallets" onRetry={refetch} />
              ) : isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Wallet className="size-6 text-gray-400" />
                    </div>
                    <p className="text-sm text-gray-500">No wallets found</p>
                  </td>
                </tr>
              ) : (
                filtered.map((w) => (
                  <tr key={w._id} className="hover:bg-gray-50 transition-colors">
                    <td data-label="Wallet No." className="px-4 py-3 font-mono text-xs text-gray-700">
                      {w.walletNumber}
                    </td>
                    <td data-label="Owner" data-cell="title" className="px-4 py-3">
                      <p className="font-medium text-gray-900">
                        {w.user?.firstName} {w.user?.lastName}
                      </p>
                      <p className="text-xs text-gray-400">{w.user?.email}</p>
                    </td>
                    <td data-label="Balance" className="px-4 py-3 text-right font-semibold text-gray-900">
                      ₦{(w.balance || 0).toLocaleString()}
                    </td>
                    <td data-label="VBA Account" data-cell="block" className="px-4 py-3">
                      {w.vba?.accountNumber ? (
                        <div>
                          <p className="text-gray-700 font-mono text-xs">{w.vba.accountNumber}</p>
                          <p className="text-xs text-gray-400">{w.vba.bankName}</p>
                        </div>
                      ) : (
                        <span className="text-gray-300 text-xs italic">Not set up</span>
                      )}
                    </td>
                    <td data-label="VBA Status" className="px-4 py-3">
                      {w.vba?.status ? (
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${VBA_STATUS_COLORS[w.vba.status] ?? "bg-gray-100 text-gray-600"}`}>
                          {w.vba.status}
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td data-label="Created" className="px-4 py-3 text-gray-500 whitespace-nowrap">
                      {new Date(w.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Page {page} of {totalPages} · {total} total
            </p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Previous
              </button>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default Wallets;
